import { FC } from "react";
import { Check, X } from "lucide-react";
import { BULLION_PRODUCT } from "@/app/alts/bullion/type-constants";
import {
    featureDisplayConfig,
    goldInvestmentFeatures,
    silverInvestmentFeatures,
    ValueDisplayType,
} from "@/app/constants/gold-table";

interface Props {
    activeBullion: string;
}

const DigitalVsPhysical: FC<Props> = ({ activeBullion }) => {
    const investmentFeatures =
        activeBullion === BULLION_PRODUCT.GOLD.TYPE
            ? goldInvestmentFeatures
            : silverInvestmentFeatures;

    const renderValue = (value: string | boolean, displayType: ValueDisplayType) => {
        switch (displayType) {
            case ValueDisplayType.CHECKMARK:
                return typeof value === "boolean" ? (
                    value ? (
                        <Check className="text-success-600" width="20px" height="20px" />
                    ) : (
                        <X className="text-error-500" width="20px" height="20px" />
                    )
                ) : (
                    value
                );

            case ValueDisplayType.BOTH:
                return typeof value === "string" ? (
                    <span>{value}</span>
                ) : value ? (
                    <Check className="text-success-600" width="20px" height="20px" />
                ) : (
                    <X className="text-error-500" width="20px" height="20px" />
                );

            default:
                return typeof value === "string" ? value : "";
        }
    };

    return (
        <div
            id="section-digital-vs-physical-gold"
            className="mb-s14 pt-6 md:mb-0 md:pt-0 [&+div.sec-in-the-news]:mt-s15"
        >
            <h2
                id="section-title"
                className="mb-s7 pb-1 text-center text-3xl font-semibold leading-[130%;] tracking-[-0.24px] md:mb-0 md:py-s15 md:text-6xl md:leading-[120%;] md:tracking-[-0.48px]"
            >
                Digital vs. Physical {activeBullion?.toLowerCase()}
            </h2>
            <div className="mx-auto w-full rounded-xl border border-neutralOpacityDark-10">
                <table id="section-table" className="w-full table-fixed border-collapse rounded-t-xl">
                    <colgroup>
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                        <col className="w-1/3" />
                    </colgroup>
                    <thead className="border-0">
                        <tr
                            id="head-row"
                            className="bg-gray-100 text-xs uppercase md:text-xl md:font-semibold md:capitalize md:text-neutral-900"
                        >
                            <th
                                id="th-feature"
                                className="rounded-tl-xl px-4 py-4 text-left text-neutral-900 md:px-6 md:py-5 md:pl-14"
                            >
                                Feature
                            </th>
                            <th
                                id="th-digital"
                                className="p-4 text-center text-neutral-900 md:p-6"
                            >
                                Digital
                            </th>
                            <th
                                id="th-physical"
                                className="rounded-tr-xl p-4 text-center text-neutral-900 md:p-6"
                            >
                                Physical
                            </th>
                        </tr>
                    </thead>
                    <tbody className="border-0">
                        {investmentFeatures.map((feature, index) => {
                            const config = featureDisplayConfig.find(
                                (config) => config.id === feature.id,
                            );

                            const displayType = config?.displayType || ValueDisplayType.TEXT;

                            return (
                                <tr
                                    id={"body-row-" + (index + 1)}
                                    key={feature.id}
                                    className={`hover:bg-gray-50 ${!index ? "[&>td]:pt-[14px] md:[&>td]:pt-6" : index === investmentFeatures.length - 1 ? "[&>td]:pb-4 md:[&>td]:pb-6" : ""}`}
                                >
                                    <td
                                        id="td-feature"
                                        className="px-4 py-2 text-left text-sm leading-[160%] tracking-[-0.18px] text-neutral-500 md:min-h-[74px] md:px-6 md:py-4 md:pl-14 md:text-lg md:font-semibold md:leading-[164%] md:text-neutral-900"
                                    >
                                        {feature.name}
                                    </td>
                                    <td
                                        id="td-digital"
                                        className="px-4 py-2 text-center text-sm leading-[164%] tracking-[-0.18px] text-neutral-900 md:px-6 md:py-4 md:text-lg md:font-medium"
                                    >
                                        <div className="flex justify-center">
                                            {renderValue(feature.digitalValue, displayType)}
                                        </div>
                                    </td>
                                    <td
                                        id="td-physical"
                                        className="px-4 py-2 text-center text-sm leading-[164%] tracking-[-0.18px] text-neutral-900 md:px-6 md:py-4 md:text-lg md:font-medium"
                                    >
                                        <div className="flex justify-center">
                                            {renderValue(feature.physicalValue, displayType)}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DigitalVsPhysical;
